import React, { useState, useCallback, useMemo } from 'react'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import { CapeeshColors } from '../../../../../assets/ColorPalette'
import renderFormattedText from '../utils/renderFormattedText'

const useStyles = makeStyles((theme) => ({
    container: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        padding: '8px 0',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
    },
    questionText: {
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 500,
        color: '#FFFFFF',
        textAlign: 'center',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        maxWidth: '100%',
    },
    optionsGrid: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        width: '100%',
    },
    optionButton: {
        width: '100%',
        padding: '14px 18px',
        borderRadius: 14,
        border: '2px solid rgba(255, 255, 255, 0.3)',
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 500,
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'all 200ms ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.16)',
            borderColor: 'rgba(255, 255, 255, 0.5)',
        },
    },
    optionCorrect: {
        borderColor: CapeeshColors.GreenBright,
        backgroundColor: 'rgba(128, 211, 135, 0.25)',
        '&:hover': {
            backgroundColor: 'rgba(128, 211, 135, 0.25)',
        },
    },
    optionIncorrect: {
        borderColor: CapeeshColors.PinkBright,
        backgroundColor: 'rgba(255, 102, 110, 0.25)',
        '&:hover': {
            backgroundColor: 'rgba(255, 102, 110, 0.25)',
        },
    },
    optionDimmed: {
        opacity: 0.4,
        cursor: 'default',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            borderColor: 'rgba(255, 255, 255, 0.3)',
        },
    },
    optionExamSelected: {
        borderColor: 'rgba(255, 255, 255, 0.45)',
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
        },
    },
}))

/**
 * MultiChoiceInteraction — alternatives as buttons, highlight on select.
 * (interactiveElementType === 1)
 */
const MultiChoiceInteraction = ({ testOption, onComplete, isExamMode, textAlign = 'center' }) => {
    const classes = useStyles()
    const [selectedIdx, setSelectedIdx] = useState(null)
    const [showFeedback, setShowFeedback] = useState(false)

    const alternatives = testOption.alternatives || []

    // Shuffle alternatives
    const shuffledOrder = useMemo(() => {
        const indices = alternatives.map((_, i) => i)
        for (let i = indices.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1))
            ;[indices[i], indices[j]] = [indices[j], indices[i]]
        }
        return indices
    }, [alternatives.length])

    const handleSelect = useCallback(
        (origIdx) => {
            if (selectedIdx !== null) return
            setSelectedIdx(origIdx)
            setShowFeedback(true)

            const alt = alternatives[origIdx]
            const isCorrect = alt?.correct === true

            // Report answer
            setTimeout(() => {
                if (onComplete) {
                    onComplete({
                        testOptionId: testOption.testOptionId,
                        answers: [
                            {
                                testOptionId: testOption.testOptionId,
                                answer: alt?.text || '',
                                correct: isCorrect,
                            },
                        ],
                    })
                }
            }, 1200)
        },
        [selectedIdx, alternatives, testOption.testOptionId, onComplete]
    )

    return (
        <div className={classes.container}>
            {testOption.text && (
                <Typography className={classes.questionText} style={{ textAlign }}>
                    {renderFormattedText(testOption.text)}
                </Typography>
            )}

            <div className={classes.optionsGrid}>
                {shuffledOrder.map((origIdx) => {
                    const alt = alternatives[origIdx]
                    if (!alt) return null

                    let extraClass = ''
                    if (showFeedback) {
                        if (isExamMode) {
                            if (origIdx === selectedIdx) {
                                extraClass = classes.optionExamSelected
                            }
                        } else {
                            if (origIdx === selectedIdx) {
                                extraClass = alt.correct
                                    ? classes.optionCorrect
                                    : classes.optionIncorrect
                            } else if (alt.correct) {
                                extraClass = classes.optionCorrect
                            } else {
                                extraClass = classes.optionDimmed
                            }
                        }
                    }

                    return (
                        <button
                            key={origIdx}
                            className={`${classes.optionButton} ${extraClass}`}
                            onClick={() => handleSelect(origIdx)}
                            disabled={selectedIdx !== null}
                        >
                            {alt.text || `Option ${origIdx + 1}`}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

export default MultiChoiceInteraction
